import { z } from 'zod';

/**
 * The JSON Schema a one-shot call asks the provider to hold its answer to, and the name it is sent
 * under.
 *
 * It carries the answer's shape only. Every bound, count and cross-field rule stays in the
 * parser beside it, which still reads every answer, so the wire never has to say more than a
 * strict provider can enforce (`strictWireProblems`).
 */
export interface AnswerWire {
  readonly name: string;
  readonly schema: Readonly<Record<string, unknown>>;
}

/** What a provider takes as a response format's name. */
const WIRE_NAME = /^[a-zA-Z0-9_-]{1,64}$/;

/**
 * The wire for an answer of `shape`, as the provider receives it: draft-7 JSON Schema of the
 * shape's input, with nothing shared by reference and no `$schema` key. Throws for a `name` a
 * provider would refuse (`WIRE_NAME`), since a wire is a constant written beside its parser.
 *
 * `shape` is built only from `z.strictObject`, `z.string()`, `z.int()`, `z.boolean()`, `z.enum`,
 * `z.literal`, `z.array`, `z.union` and `.nullable()`, with no length or count bound, so every key
 * is required and every object closed; the tests beside each wire hold it to
 * `strictWireProblems`.
 */
export const answerWire = (name: string, shape: z.ZodType): AnswerWire => {
  if (!WIRE_NAME.test(name)) {
    throw new Error(
      `a wire's name is 1 to 64 letters, digits, "_" or "-", not ${JSON.stringify(name)}`,
    );
  }
  const { $schema: _draft, ...schema } = z.toJSONSchema(shape, {
    target: 'draft-7',
    io: 'input',
    reused: 'inline',
  });

  return { name, schema };
};

/**
 * Keywords no wire uses: ones a strict provider refuses or does not enforce, every bound and
 * format — the parser beside the wire holds those — and every way of sharing or positioning a
 * schema other than `properties`, `items` and `anyOf`, which are all a wire needs.
 */
const UNSUPPORTED_KEYWORDS = [
  'oneOf',
  'allOf',
  'not',
  'if',
  'then',
  'else',
  'default',
  'minLength',
  'maxLength',
  'pattern',
  'format',
  'minItems',
  'maxItems',
  'uniqueItems',
  'contains',
  'minContains',
  'maxContains',
  'prefixItems',
  'unevaluatedItems',
  'exclusiveMinimum',
  'exclusiveMaximum',
  'multipleOf',
  'minProperties',
  'maxProperties',
  'propertyNames',
  'patternProperties',
  'dependentRequired',
  'dependentSchemas',
  'dependencies',
  'additionalItems',
  'unevaluatedProperties',
  '$ref',
  '$defs',
  'definitions',
] as const;

/**
 * The one bound a wire carries: `z.int()` states the safe-integer range as its `minimum` and
 * `maximum`, which says nothing an integer does not. It is allowed only on an integer, nullable or
 * not.
 */
const INTEGER_BOUNDS: Readonly<Record<string, number>> = {
  minimum: Number.MIN_SAFE_INTEGER,
  maximum: Number.MAX_SAFE_INTEGER,
};

/** How many objects deep a strict provider lets an answer nest. */
const MAX_WIRE_DEPTH = 10;

const isSchema = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const typesOf = (schema: Readonly<Record<string, unknown>>): unknown[] =>
  Array.isArray(schema['type']) ? schema['type'] : [schema['type']];

/**
 * Every way `schema` breaks OpenAI's strict structured output or the wire's own contract, each as
 * `<path>: <problem>`; empty for a schema the provider holds an answer to exactly. A strict wire is
 * an object at the root (`type: "object"`, nothing else) with no `anyOf` there, closes every object
 * (`additionalProperties: false`) and requires each of its properties, gives every array one `items`
 * schema, uses none of `UNSUPPORTED_KEYWORDS` and no `minimum` or `maximum` but `z.int()`'s
 * on an integer (`INTEGER_BOUNDS`), and nests no more than `MAX_WIRE_DEPTH` objects.
 */
export const strictWireProblems = (schema: Readonly<Record<string, unknown>>): string[] => {
  const problems: string[] = [];
  if (schema['type'] !== 'object') {
    problems.push('(root): is not an object');
  }
  if ('anyOf' in schema) {
    problems.push('(root): uses anyOf');
  }

  const walk = (node: Readonly<Record<string, unknown>>, path: string, depth: number): void => {
    for (const keyword of UNSUPPORTED_KEYWORDS) {
      if (keyword in node) {
        problems.push(`${path}: uses ${keyword}`);
      }
    }
    const integer = typesOf(node).includes('integer');
    for (const [keyword, bound] of Object.entries(INTEGER_BOUNDS)) {
      if (keyword in node && (!integer || node[keyword] !== bound)) {
        problems.push(`${path}: uses ${keyword}`);
      }
    }

    let objectDepth = depth;
    const properties = node['properties'];
    if (
      typesOf(node).includes('object') ||
      isSchema(properties) ||
      'additionalProperties' in node
    ) {
      objectDepth += 1;
      if (objectDepth > MAX_WIRE_DEPTH) {
        problems.push(`${path}: nests deeper than ${String(MAX_WIRE_DEPTH)} objects`);
      }
      if (node['additionalProperties'] !== false) {
        problems.push(`${path}: does not set additionalProperties: false`);
      }
      const required = Array.isArray(node['required']) ? node['required'] : [];
      for (const key of Object.keys(isSchema(properties) ? properties : {})) {
        if (!required.includes(key)) {
          problems.push(`${path}: does not require ${key}`);
        }
      }
    }

    if (isSchema(properties)) {
      for (const [key, child] of Object.entries(properties)) {
        if (isSchema(child)) {
          walk(child, `${path}.${key}`, objectDepth);
        }
      }
    }
    const items = node['items'];
    if (isSchema(items)) {
      walk(items, `${path}[]`, objectDepth);
    } else if (items !== undefined) {
      problems.push(`${path}: gives items as a list`);
    } else if (typesOf(node).includes('array')) {
      problems.push(`${path}: gives an array no items`);
    }
    const anyOf = node['anyOf'];
    if (Array.isArray(anyOf)) {
      anyOf.forEach((branch: unknown, index) => {
        if (isSchema(branch)) {
          walk(branch, `${path}.anyOf[${String(index)}]`, objectDepth);
        }
      });
    }
  };
  walk(schema, '(root)', 0);

  return problems;
};
